import React, { useEffect, useState } from 'react';
import { SaveIcon } from 'lucide-react';
import { toast } from 'sonner';
import { locationNames } from '../../data/org';
import { useHRStore } from '../../hooks/useHRStore';
import type { OfficeLocation } from '../../types/hr';
import type { Holiday, HolidayKind } from '../../types/selfService';
import { hrActions } from '../../utils/hrStore';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { TextField } from '../ui/TextField';
import { Toggle } from '../ui/Toggle';
interface HolidayFormModalProps {
  open: boolean;
  holiday: Holiday | null;
  defaultYear: number;
  onClose: () => void;
}
export function HolidayFormModal({
  open,
  holiday,
  defaultYear,
  onClose
}: HolidayFormModalProps) {
  const {
    policies,
    holidays
  } = useHRStore();
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [kind, setKind] = useState<HolidayKind>('Company');
  const [halfDay, setHalfDay] = useState(false);
  const [policyIds, setPolicyIds] = useState<string[]>([]);
  const [offices, setOffices] = useState<OfficeLocation[]>([]);
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(holiday?.name ?? '');
    setDate(holiday?.date ?? `${defaultYear}-12-01`);
    setKind(holiday?.kind ?? 'Company');
    setHalfDay(holiday?.halfDay ?? false);
    setPolicyIds(holiday?.policies ?? []);
    setOffices(holiday?.offices ?? []);
    setSubmitted(false);
  }, [open, holiday, defaultYear]);
  const duplicate = holidays.find(h => h.date === date && h.id !== holiday?.id);
  const nameError = submitted && !name.trim() ? 'Name the holiday' : undefined;
  const dateError = submitted && !date ? 'Pick a date' : duplicate ? `${duplicate.name} is already on this date` : undefined;
  const toggle = <T,>(list: T[], value: T): T[] => list.includes(value) ? list.filter(v => v !== value) : [...list, value];
  const save = () => {
    setSubmitted(true);
    if (!name.trim() || !date || duplicate) return;
    hrActions.saveHoliday({
      id: holiday?.id ?? `hol-${Date.now()}`,
      name: name.trim(),
      date,
      kind,
      halfDay,
      policies: policyIds,
      offices
    });
    toast.success(holiday ? 'Holiday updated' : 'Holiday added', {
      description: `${name.trim()} — time-off requests will skip this date${halfDay ? ' (half day)' : ''}.`
    });
    onClose();
  };
  return <Modal open={open} onClose={onClose} size="lg" title={holiday ? 'Edit holiday' : 'Add holiday'} description="Holidays are excluded from every time-off day count they apply to." footer={<>
          <Button onClick={onClose} data-id="element-119" data-mp="1ee5ilkrofa0f">Cancel</Button>
          <Button variant="primary" icon={SaveIcon} onClick={save} data-id="element-120" data-mp="1javwq2jx7vj7">
            Save holiday
          </Button>
        </>} data-id="element-118" data-mp="1o9z3psjgobtt">
      <div className="space-y-5" data-id="element-121" data-mp="38rn8hefk0iym">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-122" data-mp="38oiy1zbhwqzi">
          <TextField label="Name" value={name} onChange={setName} placeholder="e.g. Madison Company Day" error={nameError} data-id="element-123" data-mp="3do585zw11r43" />
          <TextField label="Date" type="date" value={date} onChange={setDate} error={dateError} data-id="element-124" data-mp="1200hkshuipys" />
        </div>

        <fieldset data-id="element-125" data-mp="x15ql39q5v3l">
          <legend className="text-xs font-medium text-slate-600" data-id="element-126" data-mp="1brmr8kejcgzf">Type</legend>
          <div className="mt-1.5 grid grid-cols-2 gap-2" data-id="element-127" data-mp="1btpkreewcs8w">
            {(['Federal', 'Company'] as HolidayKind[]).map(k => <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} className={`rounded-xl px-4 py-3 text-left text-sm ring-1 ring-inset transition ${kind === k ? 'bg-azure/[0.06] font-semibold text-navy ring-2 ring-azure' : 'text-slate-600 ring-slate-200 hover:ring-slate-300'}`} data-id="element-128" data-mp="1ks1kc5xqr0dh">
                {k}
                <span className="block text-xs font-normal text-muted" data-id="element-129" data-mp="diw3aw7omss">{k === 'Federal' ? 'National holiday' : 'Madison-only day'}</span>
              </button>)}
          </div>
        </fieldset>

        <div className="flex items-center justify-between rounded-xl bg-canvas px-4 py-3" data-id="element-130" data-mp="3szoaqsv06oeu">
          <div data-id="element-131" data-mp="1slj60b96g43">
            <p className="text-sm font-medium text-navy" data-id="element-132" data-mp="1s23yrczm5tr">Half day</p>
            <p className="text-xs text-muted" data-id="element-133" data-mp="6u18w5odlast">Offices close at noon — requests on this day count 0.5</p>
          </div>
          <Toggle checked={halfDay} onChange={setHalfDay} label="Half day" srOnlyLabel data-id="element-134" data-mp="brkl701ijdi4" />
        </div>

        <fieldset data-id="element-135" data-mp="3ab9jq5korae1">
          <legend className="text-xs font-medium text-slate-600" data-id="element-136" data-mp="3a7vix1ro798p">Applies to policies</legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-137" data-mp="3f6ozj3297gd3">
            <Chip on={policyIds.length === 0} onClick={() => setPolicyIds([])} data-id="element-138" data-mp="3k6uuas3tlize">
              All policies
            </Chip>
            {policies.map(p => <Chip key={p.id} on={policyIds.includes(p.id)} onClick={() => setPolicyIds(l => toggle(l, p.id))} data-id="element-139" data-mp="2rj8duj9hujv4">
                {p.name}
              </Chip>)}
          </div>
        </fieldset>

        <fieldset data-id="element-140" data-mp="1dmxaek1ugh4g">
          <legend className="text-xs font-medium text-slate-600" data-id="element-141" data-mp="1dngx0e2fvtot">Applies to offices</legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-142" data-mp="19l293fjwy3qo">
            <Chip on={offices.length === 0} onClick={() => setOffices([])} data-id="element-143" data-mp="19jhfpt9c4que">
              All offices
            </Chip>
            {locationNames.map(o => <Chip key={o} on={offices.includes(o)} onClick={() => setOffices(l => toggle(l, o))} data-id="element-144" data-mp="zmcvhyz1v5g8">
                {o}
              </Chip>)}
          </div>
        </fieldset>
      </div>
    </Modal>;
}
function Chip({
  on,
  onClick,
  children
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-pressed={on} className={`rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 ring-inset transition ${on ? 'bg-navy text-white ring-navy' : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'}`} data-id="element-145" data-mp="ltwu12lsrfhf">
      {children}
    </button>;
}